import React, { useEffect, useState } from 'react';
import { MdFilterList } from 'react-icons/md';
import omitBy from 'lodash/omitBy';
import { policySearchRequest } from '~/api';
import { useAsync, useForm, useAuth } from '~/hooks';
import { RefreshIndicator } from '~/components';
import Layout from '~/pages/templates/DefaultLayout';
import { SearchHistoryContext } from './context';
import { applyFilters } from './filters';
import { SearchHistoryGrid } from './SearchHistoryGrid';
import FilterModal from './FilterModal';
import AttachmentsModal from './AttachmentsModal';

const SearchHistory = () => {
    const { user } = useAuth();

    const [filtersOpen, setFiltersOpen] = useState(false);

    const policySearches = useAsync(async () => policySearchRequest.getAll(), false);

    const {
        state: { values: filters },
        fieldActions,
        formActions
    } = useForm({
        initialState: {
            filenumber: '',
            ownerName: '',
            submissionStart: null,
            submissionEnd: null,
            address: '',
            county: '',
            statusOptions: [
                { label: 'New Request', value: 1 },
                { label: 'Searching', value: 2 },
                { label: 'Found', value: 3 },
                { label: 'Not Found', value: 4 }
            ],
            status: null
        },
    });

    let [filteredRows, setFilteredRows] = useState([]);

    
    const clean = (filters) => {
        return omitBy(filters, (value, key) => value == null || /options/i.test(key));
    };
    
    const filterData = () => {
        const filtered = applyFilters(policySearches.value, clean(filters));
        setFilteredRows(filtered);
        setFiltersOpen(false);
    };
    
    const clearFilters = () => {
        formActions.reset();
        setFilteredRows(policySearches.value);
        setFiltersOpen(false);
    };
    
    const fetchData = async () => {
        await policySearches.execute();
    };

    const [attachmentsOpen, setAttachmentsOpen] = useState(false);
    const showAttachmentsModal = () => setAttachmentsOpen(true);
    const hideAttachmentsModal = () => setAttachmentsOpen(false);
    const [attachments, setAttachments] = useState([]);
    
    useEffect(fetchData, []);
    
    useEffect(
        () => setFilteredRows(policySearches.value),
        [policySearches.value, policySearches.status]
    );

    const ctxValue = {
        policySearches,
        filteredRows,
        filters,
        fieldActions,
        applyFilters: filterData,
        clearFilters,
        attachments,
        setAttachments,
        showAttachmentsModal,
        hideAttachmentsModal
    };

    return (
        <Layout 
            title='Lot Lookup Search History' 
            subtitle={`Search for all the prior policy requests submitted for ${user.firmName}`}
        >
            <div className="container py-4">
                <SearchHistoryContext.Provider value={ctxValue}>
                    <RefreshIndicator fetch={fetchData} />
                    <div className='mt-6'>
                        <button 
                            className='py-2 px-4 flex text-blue-dark text-sm font-semibold bg-gray-50 focus:outline-none items-center justify-center rounded-full'
                            onClick={() => setFiltersOpen(true)}
                        >
                            <MdFilterList size='1.125em' className='mr-1'/>
                            Filter
                        </button>
                    </div>
                    <SearchHistoryGrid />
                    <FilterModal isOpen={filtersOpen} setOpen={setFiltersOpen}/>
                    <AttachmentsModal isOpen={attachmentsOpen}/>
                </SearchHistoryContext.Provider>
            </div>
        </Layout>
    );
};

export default SearchHistory;